import type { WinnerItem } from '../features/Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleWinnersCarousel/RaffleWinnersCarousel';

/* The previous-winners strip and the winner videos, in one place.
 *
 * WHY THIS FILE EXISTS. All of this was already copy-pasted before anything new
 * was added: `formatPrizeLabel` in FOUR files, `formatWinnerName` and `safeDate`
 * in three, `WINNER_VIDEO_URLS` in two. These are not internal details:
 *
 *  - `formatPrizeLabel` produces the user-facing "A$10,000 in INC" string, which
 *    is precisely the copy the CODEMAP warns regresses to "BTC" after a merge
 *    from an older branch. Four definitions is four places to miss.
 *  - `formatWinnerName` is a PRIVACY rule, not formatting. It reduces a real
 *    person to "First L." before their name goes on a public page. The winners
 *    endpoint is public and returns `username` and `discordUsername` too, so a
 *    copy that drifts publishes more about a real winner than intended.
 *
 * This is the canonical copy and the one new code should import. The existing
 * four were deliberately NOT migrated here in the same change — that touches
 * RafflePage, PartnerSignupPage and DesignGiveawayPage, which is its own piece
 * of work with its own verification. So this stops the duplication growing; it
 * does not yet remove it.
 */

/** The public `/giveaway/last-winners` row shape. */
export type ApiWinner = {
  id?: string | number;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  discordUsername?: string | null;
  discordName?: string | null;
  giveawayName?: string | null;
  prizeAmount?: number | string | null;
  dateWon?: string | Date | null;
  endDate?: string | Date | null;
};

/* The jackpot headline, used only as the fallback when a row carries no prize
 * amount. Must agree with `server/lib/initialGiveawayEntries.js`'s constant of
 * the same name and with the countdown endpoint. */
const JACKPOT_PRIZE_AMOUNT_AUD = 10000;

/** The winner videos shown on /giveaway and the QR funnel. */
export const WINNER_VIDEO_URLS = [
  'https://www.youtube.com/watch?v=aXb81qXMQOw',
  'https://youtu.be/QoZ8YaLrAkE'
];

export const safeDate = (d: unknown): Date | null => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d as string);
  return Number.isNaN(x.getTime()) ? null : x;
};

export const formatPrizeLabel = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount =
    Number.isFinite(amount) && amount > 0 ? amount : JACKPOT_PRIZE_AMOUNT_AUD;

  return `A$${safeAmount.toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })} in INC`;
};

/* "First L.", or the username, or "Winner" — never a full surname.
 * See the privacy note in the file header before changing this. */
export const formatWinnerName = (w: ApiWinner) => {
  const fn = String(w.firstName || '').trim();
  const ln = String(w.lastName || '').trim();
  const username = String(w.username || '').trim();

  if (fn) {
    const initial =
      ln.length > 0 ? ln.replace('.', '').slice(0, 1).toUpperCase() : '';
    return initial ? `${fn} ${initial}.` : fn;
  }

  return username || 'Winner';
};

/** Raw endpoint payload → what `RaffleWinnersCarousel` renders. */
export const mapWinners = (data: unknown): WinnerItem[] => {
  const arr: ApiWinner[] = Array.isArray(data)
    ? (data as ApiWinner[])
    : data
      ? [data as ApiWinner]
      : [];

  return arr
    .map((w, idx) => {
      const date = safeDate(w.dateWon ?? w.endDate);
      if (!date) return null;

      const discord =
        (w.discordUsername || w.discordName || '')?.toString().trim() ||
        undefined;

      return {
        id: String(w.id ?? `${date.getTime()}-${idx}`),
        name: formatWinnerName(w),
        winningDate: date,
        prizeAmount: formatPrizeLabel(w.prizeAmount),
        discordUsername: discord
      } as WinnerItem;
    })
    .filter(Boolean) as WinnerItem[];
};
